// Ejemplos con servomotor.
import { at, part, wires, UNO } from './helpers.js';

const BB = { id: 'bb1', type: 'breadboard', x: 0, z: 75, rot: 0, props: { size: 'half' } };

export const EXAMPLES = [
  {
    id: 'servo-barrido',
    category: 'Motores',
    name: 'Servo: barrido',
    description: 'La librería Servo mueve el brazo de 0° a 180° y de regreso.',
    project: {
      name: 'Servo barrido',
      files: {
        'sketch.ino': `// Barrido: el servo va de 0° a 180° y regresa (librería Servo, señal en el pin 9).
#include <Servo.h>

Servo servo;

void setup() {
  servo.attach(9);
  Serial.begin(9600);
}

void loop() {
  for (int angulo = 0; angulo <= 180; angulo += 2) {
    servo.write(angulo);
    delay(15);
  }
  Serial.println("180°");
  for (int angulo = 180; angulo >= 0; angulo -= 2) {
    servo.write(angulo);
    delay(15);
  }
  Serial.println("0°");
}
`,
      },
      parts: [UNO, part('servo1', 'servo', { horn: 'single' }, { x: 98, z: -6, rot: 0 })],
      wires: wires([
        ['uno:9', 'servo1:PWM', 'orange'],
        ['uno:5V', 'servo1:V+', 'red'],
        ['uno:GND.2', 'servo1:GND', 'black'],
      ]),
    },
  },
  {
    id: 'servo-perilla',
    category: 'Motores',
    name: 'Servo controlado con perilla',
    description: 'El potenciómetro (A0) decide el ángulo del servo: gira la perilla con el mouse.',
    project: {
      name: 'Servo con perilla',
      files: {
        'sketch.ino': `// La perilla en A0 controla el ángulo del servo del pin 9.
#include <Servo.h>

Servo servo;

void setup() {
  servo.attach(9);
  Serial.begin(9600);
}

void loop() {
  int lectura = analogRead(A0);
  int angulo = map(lectura, 0, 1023, 0, 180);
  servo.write(angulo);
  Serial.print("Ángulo: ");
  Serial.println(angulo);
  delay(20);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('pot1', 'potentiometer', { value: 0.5 }, at(BB, 'e20', 'potentiometer', { rot: 180, pin: 'VCC' })),
        part('servo1', 'servo', { horn: 'double' }, { x: 100, z: 76, rot: 0 }),
      ],
      wires: wires([
        ['uno:5V', 'bb1:tp13', 'red'],
        ['uno:GND.2', 'bb1:tn14', 'black'],
        ['bb1:a20', 'bb1:tp16', 'red'],
        ['bb1:a22', 'bb1:tn17', 'black'],
        ['bb1:a21', 'uno:A0', 'orange'],
        ['servo1:V+', 'bb1:tp25', 'red'],
        ['servo1:GND', 'bb1:tn25', 'black'],
        ['servo1:PWM', 'uno:9', 'yellow'],
      ]),
    },
  },
  {
    id: 'servo-consumo',
    category: 'Motores',
    name: 'Servo: consumo y carga',
    description: 'Una resistencia de 1 Ω en el GND del servo hace de shunt y el Arduino mide su corriente: cámbiale la carga del brazo (ligera, pesada, bloqueado) con la simulación en marcha.',
    project: {
      name: 'Consumo del servo',
      files: {
        'sketch.ino': `// Corriente de un servo SG90. Una resistencia de 1 Ω entre el GND del servo y el de la placa hace de
// shunt: la caída de voltaje en ella es la corriente (V = I · 1 Ω), y cada paso del ADC (4.88 mV) son 4.88 mA.
// Con la simulación en marcha, selecciona el servo y cambia «Carga en el brazo» en el panel de propiedades:
// sin carga gasta ~100 mA al moverse y ~10 mA en reposo; con carga pesada, unos 420 mA y gira más lento;
// bloqueado, la corriente de atasco (~560 mA) y la simulación avisa.
#include <Servo.h>

Servo servo;
const int SHUNT = A0;

int medirMiliamperes() {
  long suma = 0;
  for (int i = 0; i < 16; i++) suma += analogRead(SHUNT);
  return suma * 4.88 / 16;      // 1 Ω: mV = mA
}

void setup() {
  servo.attach(9);
  Serial.begin(115200);
}

void loop() {
  int destino = (millis() / 1500) % 2 ? 180 : 0;   // va y viene cada 1.5 s
  servo.write(destino);
  Serial.print("mA:");
  Serial.print(medirMiliamperes());
  Serial.print(" destino:");
  Serial.println(destino);
  delay(20);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('r1', 'resistor', { value: 1 }, at(BB, 'e22', 'resistor')),
        part('servo1', 'servo', { model: 'sg90', horn: 'single', load: 'none' }, { x: 100, z: 76, rot: 0 }),
      ],
      wires: wires([
        ['uno:5V', 'bb1:tp13', 'red'],
        ['uno:GND.2', 'bb1:tn14', 'black'],
        ['servo1:V+', 'bb1:tp25', 'red'],
        ['servo1:GND', 'bb1:a22', 'brown'],
        ['bb1:a26', 'bb1:tn22', 'black'],
        ['bb1:b22', 'uno:A0', 'yellow'],
        ['servo1:PWM', 'uno:9', 'orange'],
      ]),
    },
  },
  {
    id: 'servo-mg996r',
    category: 'Motores',
    name: 'Servo MG996R con batería',
    description: 'El MG996R (engranes metálicos) puede pedir 2.5 A atascado: no va al 5V de la placa sino a 4 pilas AA con el GND en común. Una resistencia de 0.47 Ω mide su corriente.',
    project: {
      name: 'Servo MG996R',
      files: {
        'sketch.ino': `// Servo MG996R con su propia batería (4 pilas AA = 6 V) y el GND en común con el Arduino.
// Una resistencia de 0.47 Ω entre el GND del servo y el de la placa hace de shunt:
// cada paso del ADC (4.88 mV) son 10.4 mA. Sin carga gasta ~480 mA al moverse y ~10 mA en reposo.
#include <Servo.h>

Servo servo;
const int SHUNT = A0;

int medirMiliamperes() {
  long suma = 0;
  for (int i = 0; i < 16; i++) suma += analogRead(SHUNT);
  return suma * 4.88 / 16 / 0.47;   // mV entre 0.47 Ω
}

void setup() {
  servo.attach(9);
  Serial.begin(115200);
}

void loop() {
  int destino = (millis() / 2000) % 2 ? 180 : 0;   // va y viene cada 2 s
  servo.write(destino);
  Serial.print("mA:");
  Serial.print(medirMiliamperes());
  Serial.print(" destino:");
  Serial.println(destino);
  delay(20);
}
`,
      },
      parts: [
        UNO,
        BB,
        part('r1', 'resistor', { value: 0.47 }, at(BB, 'e22', 'resistor')),
        part('servo1', 'servo', { model: 'mg996r', horn: 'single', load: 'none' }, { x: 110, z: 145, rot: 0 }),
        part('bat', 'battery', { voltage: '6' }, { x: -75, z: 150, rot: 0 }),
      ],
      wires: wires([
        ['uno:GND.2', 'bb1:tn14', 'black'],
        ['bat:+', 'servo1:V+', 'red'],
        ['bat:-', 'bb1:tn18', 'black'],
        ['servo1:GND', 'bb1:a22', 'brown'],
        ['bb1:a26', 'bb1:tn22', 'black'],
        ['bb1:b22', 'uno:A0', 'yellow'],
        ['servo1:PWM', 'uno:9', 'orange'],
      ]),
    },
  },
];
